"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { HiArrowRight } from "react-icons/hi2";
import { EASE_SMOOTH } from "@/components/motion/constants";
import { cn } from "@/lib/utils";

interface HeroCTA {
  href: string;
  label: string;
  external?: boolean;
}

interface HeroCTAButtonsProps {
  primary: HeroCTA;
  secondary: HeroCTA;
  suppressEntrance?: boolean;
  scene8Reveal?: boolean;
  compact?: boolean;
}

export function HeroCTAButtons({
  primary,
  secondary,
  suppressEntrance = false,
  scene8Reveal = false,
  compact = false,
}: HeroCTAButtonsProps) {
  const reduced = useReducedMotion();
  const delay =
    scene8Reveal && !reduced ? 0.3 : reduced ? 0 : suppressEntrance ? 0 : 0.45;

  return (
    <motion.div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center",
        compact ? "gap-1" : "gap-3"
      )}
      initial={
        suppressEntrance && !scene8Reveal
          ? false
          : reduced
            ? false
            : { opacity: 0, scale: 0.95, y: 12 }
      }
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, ease: EASE_SMOOTH, delay }}
    >
      <HeroPrimaryButton
        href={primary.href}
        label={primary.label}
        external={primary.external}
        compact={compact}
      />
      <HeroSecondaryButton
        href={secondary.href}
        label={secondary.label}
        external={secondary.external}
        compact={compact}
      />
    </motion.div>
  );
}

function HeroCtaLink({
  href,
  external,
  className,
  children,
}: {
  href: string;
  external?: boolean;
  className: string;
  children: ReactNode;
}) {
  if (external) {
    return (
      <a
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}

function HeroPrimaryButton({
  href,
  label,
  external,
  compact = false,
}: HeroCTA & { compact?: boolean }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      whileHover={reduced ? undefined : { y: -2, scale: 1.02 }}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      transition={{ duration: 0.2, ease: EASE_SMOOTH }}
      className="group relative inline-flex w-full sm:w-auto"
    >
      <HeroCtaLink
        href={href}
        external={external}
        className={cn(
          "hero-btn-primary btn-shiny relative inline-flex w-full items-center justify-center gap-2 overflow-hidden",
          "rounded-xl bg-[#2E7D32] font-semibold text-white",
          compact
            ? "px-2 py-0.5 text-[0.45rem] shadow-none"
            : "btn-shiny-idle px-5 py-2.5 text-sm shadow-[0_6px_20px_rgba(46,125,50,0.35)]",
          "transition-shadow duration-300",
          !compact &&
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
          !compact && "group-hover:shadow-[0_12px_32px_rgba(46,125,50,0.45)]"
        )}
      >
        <span className="hero-btn-ripple pointer-events-none absolute inset-0" aria-hidden />
        <span>{label}</span>
        {!compact && (
          <HiArrowRight
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
            aria-hidden
          />
        )}
      </HeroCtaLink>
    </motion.div>
  );
}

function HeroSecondaryButton({
  href,
  label,
  external,
  compact = false,
}: HeroCTA & { compact?: boolean }) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      whileHover={reduced ? undefined : { y: -2, scale: 1.02 }}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      transition={{ duration: 0.2, ease: EASE_SMOOTH }}
      className="group inline-flex w-full sm:w-auto"
    >
      <HeroCtaLink
        href={href}
        external={external}
        className={cn(
          "hero-btn-secondary relative inline-flex w-full items-center justify-center overflow-hidden",
          "rounded-xl border font-semibold",
          compact
            ? "border-[#c4b8a8] bg-white px-2 py-0.5 text-[0.45rem] text-[#1B5E20]"
            : "border-white/30 bg-white/[0.06] px-5 py-2.5 text-sm text-white backdrop-blur-sm",
          "transition-all duration-300",
          !compact &&
            "hover:border-white/60 hover:bg-white hover:text-[#1B5E20]",
          !compact &&
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
        )}
      >
        <span className="hero-btn-ripple pointer-events-none absolute inset-0" aria-hidden />
        {label}
      </HeroCtaLink>
    </motion.div>
  );
}
